import type { AdditionDiscard, AdditionsAccept, CompletionStateDto, DataEntityDto, DataTypeDto, TypeRereadStateDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText } from '../../ui';

/** Le aziende per cui un'accettazione non ha potuto far partire la ricerca, in questa visita. */
const searchWaiting = new Set<string>();

/** L'avviso che la ricerca all'accettazione aspetta l'uso del motore. */
export function SearchWaiting() {
  const { tenant } = useTenant();
  if (!searchWaiting.has(tenant.id)) return null;
  return <p className="text-xs text-slate-500">Accettato. La ricerca nei documenti e nella posta già letti partirà quando sarà abilitato l’uso del motore per le letture.</p>;
}

/**
 * L'avanzamento della ricerca partita da un'accettazione (ADR 0195 § 6): la rilettura delle letture già fatte per questo
 * tipo, o il completamento delle voci. Si vede solo mentre lavora.
 */
export function SearchProgress({ type }: { type: DataTypeDto }) {
  const { isManager } = useTenant();
  const running = (f?: string) => f === 'stima' || f === 'rileggo' || f === 'completo';
  const reread = useTenantQuery<TypeRereadStateDto>(['data-model', 'reread'], '/data-model/reread', { enabled: isManager, refetchInterval: (s) => (running(s?.fase) ? 3000 : 15000) });
  const completion = useTenantQuery<CompletionStateDto>(['data-model', 'completion'], '/data-model/completion', { enabled: isManager, refetchInterval: (s) => (running(s?.fase) ? 3000 : 15000) });
  const r = reread.data;
  const c = completion.data;
  const mine = !!r?.stima?.tipi.some((x) => x.chiave === type.chiave);
  if (running(r?.fase) && (mine || r?.fase === 'stima'))
    return <p className="text-xs text-slate-500">{r!.fase === 'stima' ? 'Cerco nelle letture già fatte i documenti e i messaggi che ne parlano…' : `Rileggo le letture già fatte: ${r!.fatti} di ${r!.totale}, ${r!.voci} voci trovate.`}</p>;
  if (running(c?.fase)) return <p className="text-xs text-slate-500">Cerco i valori mancanti delle voci accettate: {c!.fatti} di {c!.totale}, {c!.valori} valori trovati.</p>;
  if (r?.fase === 'fermo' && mine) return <p className="text-xs text-amber-700">La ricerca si è fermata al tetto di spesa: riparte da dove era quando il tetto lo permette.</p>;
  return null;
}

/** Accetta e scarta le aggiunte, poi rilegge il modello (il menu, la pagina del tipo e la scheda). */
export function useAdditionActions() {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (path: 'accept' | 'discard', body: AdditionsAccept | AdditionDiscard) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ ricerca?: string }>(t(`/model/additions/${path}`), { body });
      // La ricerca all'accettazione usa il motore: finché quell'uso non è abilitato (ADR 0201) non parte, e lo si dice.
      if (r.ricerca === 'motore_non_abilitato') searchWaiting.add(tenant.id);
      await queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'data-model'] });
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, accept: (b: AdditionsAccept) => run('accept', b), discard: (b: AdditionDiscard) => run('discard', b) };
}

/**
 * Le aggiunte di un tipo (ADR 0195 § 6): il tipo stesso, i suoi attributi e le sue voci trovati dalle letture, che valgono
 * subito e aspettano la persona. «Accetta tutto» accetta ciò che la pagina mostrava quando è stata caricata (`seenAt`), non
 * ciò che arriva dopo; uno scarto si fa sempre voce per voce e resta ricordato.
 */
export function TypeAdditions({ type, seenAt }: { type: DataTypeDto; seenAt: number }) {
  const { tenant, isManager } = useTenant();
  const { busy, error, accept, discard } = useAdditionActions();
  const typeAdded = !!type.aggiunto && !type.aggiunto.accettato_il;
  const attrs = type.attributi.filter((a) => a.aggiunto && !a.aggiunto.accettato_il);
  // Le voci aggiunte, ognuna con Accetta e Scarta qui: nell'elenco possono essere lontane o nascoste da un filtro.
  const added = type.entita.filter((e) => e.aggiunta);
  const total = (typeAdded ? 1 : 0) + attrs.length + added.length;
  if (!total) return null;
  const fino = new Date(seenAt).toISOString();
  return (
    <Card>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            Aggiunto dalle letture <Badge tone="blue">{total}</Badge>
          </h2>
          <p className="mt-1 text-xs text-slate-500">Vale già e si usa. Accetta per togliere il segno, scarta ciò che non è dell’azienda: non tornerà.</p>
        </div>
        {isManager && (
          <Button disabled={busy} onClick={() => void accept({ tipi: [type.chiave], fino })}>
            Accetta tutto ({total})
          </Button>
        )}
      </div>
      <ul className="mt-3 divide-y divide-slate-100 text-sm">
        {typeAdded && (
          <li className="flex flex-wrap items-center gap-2 py-2">
            <span className="min-w-0 flex-1">
              Il tipo «{type.etichetta}» è nato da {type.aggiunto!.prove.length} fonti: {type.aggiunto!.prove.join(' · ')}
            </span>
            {isManager && (
              <Button variant="ghost" disabled={busy} onClick={() => void discard({ cosa: 'tipo', tipo: type.chiave })}>
                Scarta il tipo
              </Button>
            )}
          </li>
        )}
        {attrs.map((a) => (
          <li key={a.nome} className="flex flex-wrap items-center gap-2 py-2">
            <span className="min-w-0 flex-1">
              Campo «{a.etichetta}», trovato in {a.aggiunto!.fonte} il {dateTime(a.aggiunto!.il)}
            </span>
            {isManager && (
              <>
                <Button variant="ghost" disabled={busy} onClick={() => void accept({ tipi: [type.chiave], attributo: a.nome })}>
                  Accetta
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => void discard({ cosa: 'attributo', tipo: type.chiave, nome: a.nome })}>
                  Scarta
                </Button>
              </>
            )}
          </li>
        ))}
        {added.map((e) => (
          <li key={e.rif} className="flex flex-wrap items-center gap-2 py-2">
            <span className="min-w-0 flex-1">
              Voce{' '}
              <a className="text-accent hover:underline" href={`#/t/${tenant.id}/e/${type.chiave}/${encodeURIComponent(e.rif)}`}>
                «{e.nome || String(e.attributi.nome ?? e.rif)}»
              </a>
              , trovata in {e.aggiunta!.fonte} il {dateTime(e.aggiunta!.il)}
            </span>
            {isManager && (
              <>
                <Button variant="ghost" disabled={busy} onClick={() => void accept({ tipi: [type.chiave], rif: [e.rif] })}>
                  Accetta
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => void discard({ cosa: 'voce', tipo: type.chiave, nome: e.rif })}>
                  Scarta
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
      {error && <ErrorText>{error}</ErrorText>}
    </Card>
  );
}

/** Il segno di una voce aggiunta nella sua scheda, con Accetta e Scarta (lo scarto resta ricordato). */
export function EntityAddition({ type, entity, onDiscarded }: { type: DataTypeDto; entity: DataEntityDto; onDiscarded: () => void }) {
  const { isManager } = useTenant();
  const { busy, error, accept, discard } = useAdditionActions();
  if (!entity.aggiunta) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
      <span className="min-w-0 flex-1">
        Aggiunta dalle letture: {entity.aggiunta.fonte}, il {dateTime(entity.aggiunta.il)}
      </span>
      {isManager && (
        <>
          <Button disabled={busy} onClick={() => void accept({ tipi: [type.chiave], rif: [entity.rif] })}>
            Accetta
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() =>
              void discard({ cosa: 'voce', tipo: type.chiave, nome: entity.rif }).then((ok) => {
                if (ok) onDiscarded();
              })
            }
          >
            Scarta
          </Button>
        </>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}
